/**
 * The artifact rail: compose a table, watch it generate, open a version.
 *
 * Generation is queued, not awaited — a table over a large corpus is minutes of work
 * and dozens of model calls, and a request that long would die to any proxy timeout.
 * So this polls, and shows `queued → running → ready` honestly, including `partial`
 * when a budget cap stopped the run early. A partial artifact that says so is useful;
 * one that quietly stops short is a false negative wearing a finished coat.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, type ArtifactSummary, type Identity } from "../lib/api";
import { go } from "../lib/route";
import { useSheet } from "../lib/sheet";

const POLL_MS = 2500;

interface Props {
  datasetId: string;
  /** A chat message this table should record as its origin, if it came from one. */
  seed: { messageId: string; question: string } | null;
  onSeedUsed(): void;
  /** Whether the rail is showing. Only ever false where it is a sheet: on a wide
   * screen it is a column that is always there, and the value is ignored. */
  open: boolean;
  onClose(): void;
}

const SOFT_CATEGORIES = ["everyday_life", "relatives", "war_stance"] as const;

export function ArtifactRail({ datasetId, seed, onSeedUsed, open, onClose }: Props) {
  const panel = useRef<HTMLElement>(null);
  const sheet = useSheet(panel, open, onClose);
  const [artifacts, setArtifacts] = useState<ArtifactSummary[] | null>(null);
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  // inference-claims task 6.2: a second, separate compose form — an inference ask
  // has a different shape entirely (ask kind, identity, categories, the
  // participant-only opt-in), not a prompt.
  const [composingInference, setComposingInference] = useState(false);
  const [inferenceTitle, setInferenceTitle] = useState("");
  const [ask, setAsk] = useState<"soft_claims" | "relations">("soft_claims");
  const [identity, setIdentity] = useState("");
  const [categories, setCategories] = useState<string[]>([...SOFT_CATEGORIES]);
  // Unchecked by default: mention-only stays included unless the operator opts out
  // explicitly (design D6) — the checkbox exists precisely so that opt-out is a
  // visible, deliberate act rather than a silent default.
  const [participantOnly, setParticipantOnly] = useState(false);

  // identity-dossiers task 2.4: a third, separate compose form. A dossier subject is
  // either a communications handle (unchanged, dossier-artifacts) or a resolved
  // identity cluster (design D1) — picked from the same `entity_members_current`
  // projection the Identities view already lists, never typed by hand, so a request
  // can never name a cluster id the identity view could not also show.
  const [composingDossier, setComposingDossier] = useState(false);
  const [dossierTitle, setDossierTitle] = useState("");
  const [dossierSubjectKind, setDossierSubjectKind] = useState<"handle" | "identity_cluster">(
    "identity_cluster",
  );
  const [dossierHandle, setDossierHandle] = useState("");
  const [dossierClusterId, setDossierClusterId] = useState("");
  const [identityOptions, setIdentityOptions] = useState<Identity[] | null>(null);

  const refresh = useCallback(async () => {
    try {
      setArtifacts(await api.artifacts(datasetId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, [datasetId]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    if (!seed) return;
    setComposing(true);
    setTitle(seed.question.slice(0, 80));
    setPrompt(seed.question);
    onSeedUsed();
  }, [seed, onSeedUsed]);

  useEffect(() => {
    if (!composingDossier || dossierSubjectKind !== "identity_cluster") return;
    let cancelled = false;
    void api
      .identities(datasetId)
      .then((rows) => {
        if (!cancelled) setIdentityOptions(rows);
      })
      .catch((cause) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [composingDossier, dossierSubjectKind, datasetId]);

  async function generateDossier(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api.generateDossier(
        datasetId,
        dossierTitle.trim(),
        dossierSubjectKind === "handle"
          ? { kind: "handle", identity: dossierHandle.trim() }
          : { kind: "identity_cluster", clusterId: dossierClusterId },
      );
      setComposingDossier(false);
      setDossierTitle("");
      setDossierHandle("");
      setDossierClusterId("");
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function generate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api.generate(datasetId, { title: title.trim(), prompt: prompt.trim() });
      setComposing(false);
      setTitle("");
      setPrompt("");
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function generateInferenceAsk(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api.generateInference(datasetId, {
        title: inferenceTitle.trim(),
        ask,
        identity: ask === "soft_claims" ? identity.trim() : identity.trim() || undefined,
        categories: ask === "soft_claims" ? categories : undefined,
        participantOnly,
      });
      setComposingInference(false);
      setInferenceTitle("");
      setIdentity("");
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  function toggleCategory(category: string) {
    setCategories((current) =>
      current.includes(category)
        ? current.filter((c) => c !== category)
        : [...current, category],
    );
  }

  return (
    <aside
      className={open ? "rail sheet-open" : "rail"}
      data-testid="artifact-rail"
      ref={panel}
      tabIndex={-1}
      aria-label="Artifacts"
      role={sheet ? "dialog" : undefined}
      aria-modal={sheet ? true : undefined}
    >
      <header className="row sheet-head" style={{ justifyContent: "space-between" }}>
        <p className="eyebrow">Artifacts</p>
        {sheet && (
          <button className="sheet-close" onClick={onClose} aria-label="Close artifacts">
            ×
          </button>
        )}
        <button onClick={() => setComposing((v) => !v)} data-testid="compose-toggle">
          {composing ? "Cancel" : "New table"}
        </button>
        <button
          onClick={() => setComposingInference((v) => !v)}
          data-testid="compose-inference-toggle"
        >
          {composingInference ? "Cancel" : "New inference run"}
        </button>
        <button
          onClick={() => setComposingDossier((v) => !v)}
          data-testid="compose-dossier-toggle"
        >
          {composingDossier ? "Cancel" : "New dossier"}
        </button>
      </header>

      {composing && (
        <form onSubmit={generate} className="card compose" data-testid="compose-form">
          <label htmlFor="artifact-title" className="eyebrow">
            Title
          </label>
          <input
            id="artifact-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <label htmlFor="artifact-prompt" className="eyebrow">
            What should each row be?
          </label>
          <textarea
            id="artifact-prompt"
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="One row per person mentioned, with their handle, first appearance and message count."
            required
          />
          <button className="primary" type="submit">
            Generate
          </button>
        </form>
      )}

      {composingInference && (
        <form
          onSubmit={generateInferenceAsk}
          className="card compose"
          data-testid="compose-inference-form"
        >
          <p style={{ color: "var(--ink-faint)" }}>
            The least certain output this product makes. Every claim will re-ground to
            exact characters or not be stored at all.
          </p>
          <label htmlFor="inference-title" className="eyebrow">
            Title
          </label>
          <input
            id="inference-title"
            value={inferenceTitle}
            onChange={(e) => setInferenceTitle(e.target.value)}
            required
          />
          <label htmlFor="inference-ask" className="eyebrow">
            Ask
          </label>
          <select
            id="inference-ask"
            value={ask}
            onChange={(e) => setAsk(e.target.value as "soft_claims" | "relations")}
            data-testid="inference-ask-select"
          >
            <option value="soft_claims">Soft claims (about one identity)</option>
            <option value="relations">Relations (single-hop, whole corpus)</option>
          </select>

          <label htmlFor="inference-identity" className="eyebrow">
            Identity{ask === "relations" ? " (optional — scopes the corpus read)" : ""}
          </label>
          <input
            id="inference-identity"
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
            required={ask === "soft_claims"}
          />

          {ask === "soft_claims" && (
            <fieldset data-testid="inference-categories">
              <legend className="eyebrow">Categories</legend>
              {SOFT_CATEGORIES.map((category) => (
                <label key={category} className="row">
                  <input
                    type="checkbox"
                    checked={categories.includes(category)}
                    onChange={() => toggleCategory(category)}
                  />
                  {category}
                </label>
              ))}
            </fieldset>
          )}

          <label className="row">
            <input
              type="checkbox"
              data-testid="participant-only-checkbox"
              checked={participantOnly}
              onChange={(e) => setParticipantOnly(e.target.checked)}
            />
            Participant-only (excludes mention-only windows — off by default)
          </label>

          <button className="primary" type="submit">
            Generate
          </button>
        </form>
      )}

      {composingDossier && (
        <form onSubmit={generateDossier} className="card compose" data-testid="compose-dossier-form">
          <p style={{ color: "var(--ink-faint)" }}>
            A per-subject profile over facets this dataset already measures — no
            narrative, every row cited or named absent.
          </p>
          <label htmlFor="dossier-title" className="eyebrow">
            Title
          </label>
          <input
            id="dossier-title"
            value={dossierTitle}
            onChange={(e) => setDossierTitle(e.target.value)}
            required
          />
          <label htmlFor="dossier-subject-kind" className="eyebrow">
            Subject
          </label>
          <select
            id="dossier-subject-kind"
            value={dossierSubjectKind}
            onChange={(e) =>
              setDossierSubjectKind(e.target.value as "handle" | "identity_cluster")
            }
            data-testid="dossier-subject-kind-select"
          >
            <option value="identity_cluster">Resolved identity</option>
            <option value="handle">Communications handle</option>
          </select>

          {dossierSubjectKind === "identity_cluster" ? (
            <>
              <label htmlFor="dossier-identity-picker" className="eyebrow">
                Identity
              </label>
              <select
                id="dossier-identity-picker"
                value={dossierClusterId}
                onChange={(e) => setDossierClusterId(e.target.value)}
                data-testid="dossier-identity-picker"
                required
              >
                <option value="" disabled>
                  Select a resolved identity…
                </option>
                {identityOptions?.map((identity) => (
                  <option key={identity.cluster_id} value={identity.cluster_id}>
                    {identity.display_value} ({identity.state}
                    {identity.non_name_shape ? " · non-name shape" : ""})
                  </option>
                ))}
              </select>
              {identityOptions?.length === 0 && (
                <p data-testid="dossier-no-identities" style={{ color: "var(--ink-faint)" }}>
                  No resolved identities in this dataset yet — run resolution first.
                </p>
              )}
            </>
          ) : (
            <>
              <label htmlFor="dossier-handle" className="eyebrow">
                Communications handle
              </label>
              <input
                id="dossier-handle"
                value={dossierHandle}
                onChange={(e) => setDossierHandle(e.target.value)}
                required
              />
            </>
          )}

          <button className="primary" type="submit">
            Generate
          </button>
        </form>
      )}

      {error && (
        <p data-testid="rail-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      <ul className="artifact-list" data-testid="artifact-list">
        {artifacts?.map((artifact) => (
          <li key={artifact.id} className="card">
            <button
              className="artifact-link"
              data-testid="artifact-link"
              disabled={!artifact.version_id}
              onClick={() =>
                go(
                  artifact.kind === "dossier"
                    ? `/d/${datasetId}/dossier/${artifact.id}/${artifact.version_id ?? ""}`
                    : artifact.kind === "inference"
                      ? `/d/${datasetId}/inference/${artifact.id}/${artifact.version_id ?? ""}`
                      : `/d/${datasetId}/a/${artifact.id}/${artifact.version_id ?? ""}`,
                )
              }
            >
              <strong>{artifact.title}</strong>
              <span className="eyebrow">
                v{artifact.version_no ?? "?"} · {artifact.status ?? "queued"}
              </span>
            </button>
            {artifact.stale && (
              // Staleness is computed, not stored: the dataset's ingest sequence has
              // moved past the watermark this version was built at. A stored flag
              // would drift the first time an invalidation was missed.
              <p className="stale" data-testid="artifact-stale">
                Files were added since this was built (seq {artifact.corpus_watermark} →{" "}
                {artifact.current_ingest_seq}). It is still correct about what it saw.
              </p>
            )}
          </li>
        ))}
      </ul>

      {artifacts?.length === 0 && (
        <p style={{ color: "var(--ink-faint)" }} data-testid="artifacts-empty">
          No artifacts yet.
        </p>
      )}
    </aside>
  );
}
